import { c as _c } from "react/compiler-runtime";
import TrailerCarouselImageOverlay from '@/app/[locale]/(worlds)/[roomId]/components/TrailerCarouselImageOverlay';
import TrailerCarouselVideo from '@/app/[locale]/(worlds)/[roomId]/components/TrailerCarouselVideo';
import ArrowLeftIcon from '@/components/assets/icon_arrow_left.svg';
import OptimizedImage from '@/components/OptimizedImage';
import ViverseSkeleton from '@/components/ViverseSkeleton';
import { useCheckEnterRoomContext } from '@/context/CheckEnterRoomContext';
import { MEDIA_TYPE } from '@/hooks/useTrailerViewMedia';
import { AnimatePresence, motion } from 'framer-motion';
import { useCallback, useEffect, useState } from 'react';
import { useEventListener } from 'usehooks-ts';
export interface Media {
  type: (typeof MEDIA_TYPE)[keyof typeof MEDIA_TYPE];
  url: string;
  duration?: number;
  playbackToken?: string;
  dashUri?: string;
  hlsUri?: string;
  title?: string;
  coverImage?: string;
  isPlaceholder?: boolean;
}
interface TrailerCarouselSlideProps {
  media: Media;
  onEnded: () => void;
  mediaList: Media[];
  currentIndex: number;
  setCurrentIndex: React.Dispatch<React.SetStateAction<number>>;
  playKey: number;
}
const TrailerCarouselSlide = t0 => {
  const $ = _c(38);
  const {
    media,
    onEnded,
    mediaList,
    currentIndex,
    setCurrentIndex,
    playKey
  } = t0;
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  let t1;
  if ($[0] !== media) {
    t1 = media || {};
    $[0] = media;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    type,
    url,
    playbackToken
  } = t1;
  const {
    roomData
  } = useCheckEnterRoomContext();
  let t2;
  if ($[2] !== mediaList || $[3] !== setCurrentIndex) {
    t2 = move => {
      setCurrentIndex(prev => {
        const nextIndex = prev + move;
        if (nextIndex < 0) {
          return mediaList.length - 1;
        } else {
          if (nextIndex >= mediaList.length) {
            return 0;
          } else {
            return nextIndex;
          }
        }
      });
    };
    $[2] = mediaList;
    $[3] = setCurrentIndex;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const handleClickArrow = t2;
  let t3;
  if ($[5] !== mediaList?.length || $[6] !== type) {
    t3 = () => {
      const hasItems = mediaList?.length > 1;
      const isImage = type === MEDIA_TYPE.IMAGE;
      setCanScrollLeft(hasItems && isImage);
      setCanScrollRight(hasItems && isImage);
    };
    $[5] = mediaList?.length;
    $[6] = type;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== currentIndex || $[9] !== mediaList || $[10] !== type) {
    t4 = [currentIndex, mediaList, type];
    $[8] = currentIndex;
    $[9] = mediaList;
    $[10] = type;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  useEffect(t3, t4);
  let t5;
  if ($[12] !== onEnded) {
    t5 = event => {
      if (event.origin !== "https://showroom.one.blendvision.com") {
        return;
      }
      let message = event.data;
      if (typeof message === "string") {
        try {
          message = JSON.parse(message);
        } catch {
          return;
        }
      }
      const playlogEvent = message?.info?.playlogEventName;
      if (playlogEvent === "playback_video_ended") {
        onEnded();
      }
    };
    $[12] = onEnded;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  const handleMessage = t5;
  useEventListener("message", handleMessage);
  if (media.isPlaceholder) {
    const showRoomCover = Boolean(roomData.image);
    let t6;
    if ($[14] !== roomData.image || $[15] !== showRoomCover) {
      t6 = showRoomCover ? <OptimizedImage src={roomData.image} alt="" fill={true} loading="eager" fetchPriority="high" sizes="(max-width: 1023px) 100vw, (max-width: 1439px) 66vw, (max-width: 1599px) 60vw, 1296px" className="z-base object-cover opacity-20 lg:opacity-100" /> : <ViverseSkeleton aria-hidden={true} className="absolute inset-0 rounded-none" />;
      $[14] = roomData.image;
      $[15] = showRoomCover;
      $[16] = t6;
    } else {
      t6 = $[16];
    }
    const t7 = roomData?.image;
    const t8 = !showRoomCover;
    let t9;
    if ($[17] !== t7 || $[18] !== t8) {
      t9 = <TrailerCarouselImageOverlay url={t7} eager={t8} />;
      $[17] = t7;
      $[18] = t8;
      $[19] = t9;
    } else {
      t9 = $[19];
    }
    let t10;
    if ($[20] !== t6 || $[21] !== t9) {
      t10 = <>{t6}{t9}</>;
      $[20] = t6;
      $[21] = t9;
      $[22] = t10;
    } else {
      t10 = $[22];
    }
    return t10;
  }
  if (!url) {
    return null;
  }
  let t6;
  if ($[23] !== canScrollLeft || $[24] !== canScrollRight || $[25] !== currentIndex || $[26] !== handleClickArrow || $[27] !== media.coverImage || $[28] !== media.dashUri || $[29] !== media.hlsUri || $[30] !== media.title || $[31] !== onEnded || $[32] !== playKey || $[33] !== playbackToken || $[34] !== roomData?.image || $[35] !== type || $[36] !== url) {
    t6 = <>{type === MEDIA_TYPE.VIDEO ? <div key={playKey} className="h-full w-full"><TrailerCarouselVideo url={url} playbackToken={playbackToken} dashUri={media.dashUri} hlsUri={media.hlsUri} title={media.title} coverImage={media.coverImage} onEnded={onEnded} /></div> : <><AnimatePresence mode="wait"><motion.div key={url} initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} exit={{
            opacity: 0
          }} transition={{
            duration: 0.3
          }} className="absolute inset-0"><OptimizedImage src={url} alt="trailer-carousel-slide" fill={true} loading={currentIndex === 0 ? "eager" : "lazy"} fetchPriority={currentIndex === 0 ? "high" : "auto"} sizes="(max-width: 1023px) 100vw, (max-width: 1439px) 66vw, (max-width: 1599px) 60vw, 1296px" className="z-base object-cover opacity-20 lg:opacity-100" /></motion.div></AnimatePresence><TrailerCarouselImageOverlay url={roomData?.image} />{canScrollLeft && <ArrowLeftIcon className="absolute top-1/2 left-0 m-5 h-6 w-6 -translate-y-1/2 cursor-pointer max-lg:hidden" onClick={() => handleClickArrow(-1)} />}{canScrollRight && <ArrowLeftIcon className="absolute top-1/2 right-0 m-5 h-6 w-6 -translate-y-1/2 rotate-180 cursor-pointer max-lg:hidden" onClick={() => handleClickArrow(1)} />}</>}</>;
    $[23] = canScrollLeft;
    $[24] = canScrollRight;
    $[25] = currentIndex;
    $[26] = handleClickArrow;
    $[27] = media.coverImage;
    $[28] = media.dashUri;
    $[29] = media.hlsUri;
    $[30] = media.title;
    $[31] = onEnded;
    $[32] = playKey;
    $[33] = playbackToken;
    $[34] = roomData?.image;
    $[35] = type;
    $[36] = url;
    $[37] = t6;
  } else {
    t6 = $[37];
  }
  return t6;
};
export default TrailerCarouselSlide;